import { Label, Modal, Text } from '@thefarmersfront/kitchen';

import { Table } from '~/components/common';

import { useUserInfoStore } from '~/store';
import { formatTel } from '~/utils';
import { getOrderDetailDeliveryInfoColumnDefs, getOrderDetailProductInfoColumnDefs } from '~/utils/order';

import type {
  TypeOrderDetailDeliveryInfoData,
  TypeOrderDetailOrderInfoData,
  TypeOrderOrderCode,
} from '~/types/order.types';

import styles from './OrderDetailModal.module.scss';

type TypeOrderDetailModal = {
  open: boolean;
  data: TypeOrderOrderCode | null;
  onConfirm: () => Promise<void>;
};

const OrderDetailModal = ({ open, data, onConfirm }: TypeOrderDetailModal) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const canAccessUserInfo = hasPermission([{ ownerType: '3PL' }, { ownerType: 'NFA' }]);

  if (!data) return null;

  const orderInfoData: TypeOrderDetailOrderInfoData[] = data.order?.orderItems ?? [];

  const deliveryInfoData: TypeOrderDetailDeliveryInfoData[] =
    data.delivery?.invoices.map(({ invoiceNumber, deliveryStatus, invoiceTrackingUrl }) => ({
      invoiceNumber,
      operation: data.delivery.operation?.name,
      courier: data.delivery.courier?.name,
      deliveryStatus: deliveryStatus?.name,
      url: invoiceTrackingUrl,
    })) ?? [];

  const { columnDefs: productInfoColumnDefs } = getOrderDetailProductInfoColumnDefs();
  const { columnDefs: deliveryInfoColumnDefs } = getOrderDetailDeliveryInfoColumnDefs();

  return (
    <Modal title="출고 주문 상세" size="xlarge" open={open} onConfirm={onConfirm}>
      <div className={styles['order-detail-modal-content']}>
        {canAccessUserInfo && (
          <div className={styles['content-row']}>
            <div className={styles['content-section']}>
              <Text size="xxlarge" weight="bold">
                주문자 정보
              </Text>
              <div className={styles['orderer-info']}>
                <div className={styles['info-data']}>
                  <Label title="주문자명" />
                  <Text size="large">{data?.orderer?.name || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="주문자 ID" />
                  <Text size="large">{data?.orderer?.id || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="연락처" />
                  <Text size="large">{data?.orderer?.phoneNumber ? formatTel(data?.orderer?.phoneNumber) : '-'}</Text>
                </div>
              </div>
            </div>
            <div className={styles['content-section']}>
              <Text size="xxlarge" weight="bold">
                수령자 정보
              </Text>
              <div className={styles['receiver-info']}>
                <div className={styles['info-data']}>
                  <Label title="수령자명" />
                  <Text size="large">{data?.receiver?.name || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="연락처" />
                  <Text size="large">{data?.receiver?.phoneNumber ? formatTel(data?.receiver?.phoneNumber) : '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="기본주소" />
                  <Text size="large">{data?.receiver?.address?.primaryAddress || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="상세주소" />
                  <Text size="large">{data?.receiver?.address?.secondAddress || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="수령장소" />
                  <Text size="large">{data?.receiver?.deliveryRequest?.pickupPlaceType.name || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="수령장소 상세" />
                  <Text size="large">{data?.receiver?.deliveryRequest?.pickupDetail || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="출입방법" />
                  <Text size="large">{data?.receiver?.deliveryRequest?.accessMethod.name || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="출입방법 상세" />
                  <Text size="large">{data?.receiver?.deliveryRequest?.accessDetail || '-'}</Text>
                </div>
                <div className={styles['info-data']}>
                  <Label title="배송요청사항" />
                  <Text size="large">{data?.receiver?.deliveryRequest?.deliveryMessage || '-'}</Text>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={styles['content-section']}>
          <Text size="xxlarge" weight="bold">
            상품 정보
          </Text>
          <div className={styles['view-table-wrapper']}>
            <Table
              data={orderInfoData}
              rowStatusType="SEARCH_EMPTY"
              options={{
                columnDefs: productInfoColumnDefs,
                domLayout: 'autoHeight',
              }}
            />
          </div>
        </div>

        <div className={styles['content-section']}>
          <Text size="xxlarge" weight="bold">
            배송 정보
          </Text>
          <div className={styles['view-table-wrapper']}>
            <Table
              data={deliveryInfoData}
              rowStatusType="SEARCH_EMPTY"
              options={{
                columnDefs: deliveryInfoColumnDefs,
                domLayout: 'autoHeight',
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrderDetailModal;
